import {
    createApp as createClientApp,
    h,
    VNode,
    resolveDynamicComponent,
    Transition,
    App,
    PropType
} from 'vue'
import {RouterView} from 'vue-router'
import {createHead} from '@vueuse/head'
import router from './router'
import './registerServiceWorker'
import store, {ActionTypes} from './store'
import hasNestedRouterLink from './directives/has-nested-router-link'
import background from './directives/background'
import tooltip from './directives/tooltip'
import VueTippy from 'vue-tippy'


import 'simplebar'
import '@purge-icons/generated'
import 'nprogress/nprogress.css'
import '@vueform/multiselect/themes/default.scss'
import '@vueform/slider/themes/default.scss'
import 'simplebar/dist/simplebar.css'
import 'tiny-slider/src/tiny-slider.scss'
import 'notyf/notyf.min.css'
import 'tippy.js/dist/tippy.css'
import 'tippy.js/dist/svg-arrow.css'
import 'tippy.js/dist/border.css'
import 'tippy.js/dist/backdrop.css'
import 'tippy.js/themes/light.css'

import './scss/vendors/font-awesome-v5.css'
import './scss/vendors/line-icons-pro.css'
import './scss/vendors/prism-coldark-cold.css'

import './scss/main.scss'
import {User} from "./types";
import {mapActions, mapState} from "vuex";
import {VueClipboard} from '@soerenmartius/vue3-clipboard'
import moment from 'moment'
import {Notyf} from 'notyf';

const notyf = new Notyf({
    duration: 30000,
    position: {
        x: 'right',
        y: 'top',
    },
    types: [
        {
            type: 'warning',
            background: 'orange',
        },
        {
            type: 'error',
            background: 'indianred',
            duration: 30000,
            dismissible: true
        }
    ]
});
import setupInterceptors from './services/setupInterceptors';

setupInterceptors(store);

type VueroAppOptions = {
    enhanceApp?: (app: App) => Promise<void>
}

export async function createApp({enhanceApp}: VueroAppOptions) {
    const head = createHead()

    const app = createClientApp({
        // This is the global app setup function
        props: {
            user: {
                type: Object as PropType<User>
            }
        },
        computed: {
            ...mapState([
                'user'
            ])
        },

        methods: {
            ...mapActions({
                fetchNotifications: ActionTypes.FETCH_NOTIFICATIONS // Mapping Vuex actions
            }),

            async fetchNotifications_all() {
                try {
                    const response = await this.fetchNotifications();
                    this.showNotifications(response);
                } catch (error) {
                    //console.error('Error fetching notifications:', error);
                    //notyf.error('Failed to fetch notifications');
                }
            },
            showNotifications(notifications: any) {
                notifications.forEach((notification: any) => {
                    notyf.open({
                        type: 'warning',
                        message: notification.message
                    });

                    Notification.requestPermission().then(function (result) {
                        if (result === 'granted') {
                            navigator.serviceWorker.ready.then(function (registration) {
                                registration.showNotification(notification.message);
                            });
                        }
                    });
                });
            }
        },

        mounted() {
            window.setInterval(() => {
                this.fetchNotifications_all();
            }, 50000);

        },

        setup() {
            /**
             * We provide user state and api to our entire app
             * We can then inject them later in any component
             *
             * @see /@src/composable/useApi and /@src/composable/useUserSession
             */


            /**
             * Here we are creating a render function for our router view with
             * a Transition for the inner component
             * Template equivalent would be:
             *
             * <RouterView v-slot="{ Component }">
             *   <transition name="fade-slow" mode="out-in">
             *     <component :is="Component" />
             *   </transition>
             * </RouterView>
             */
            return () => {
                const defaultSlot = ({Component: _Component}: any) => {
                    const Component = resolveDynamicComponent(_Component) as VNode

                    return [
                        h(
                            Transition,
                            {name: 'fade-fast', mode: 'out-in'},
                            {
                                default: () => [h(Component)],
                            }
                        ),
                    ]
                }

                return h(RouterView, null, {
                    default: defaultSlot,
                })
            }
        },

    })

    /**
     * Here we are setting up router navigation guards
     *
     * We can add meta to pages either by declaring them manualy in the
     * routes declaration (see /@src/router.ts)
     * or by adding a <route> tag into .vue files (see /@src/pages/sidebar/dashboards.ts)
     *
     * <route lang="yaml">
     * meta:
     *   requiresAuth: true
     * </route>
     *
     * <script setup lang="ts">
     *  // TS script
     * </script>
     *
     * <template>
     *  // HTML content
     * </template>
     */
    /**
     * @params {date} date to be converted to timeago
     * @returns returns timeAgo
     */
    app.config.globalProperties.$filters = {
        timeAgo(index_name: any, date: any) {
            const current = moment().startOf('day')
            const given = moment(date, "YYYY-MM-DD")
            return Math.abs(moment.duration(given.diff(current)).asDays())
        },
    }


    app.use(VueClipboard)
    app.use(head)
    app.use(store)
    app.use(router)


    if (enhanceApp) {
        await enhanceApp(app)
    }

    return app
}

createApp({

    async enhanceApp(app) {
        app.use(VueTippy, {
            defaultProps: {
                theme: 'dark',
            },
        })
        app.directive('has-nested-router-link', hasNestedRouterLink)
        app.directive('background', background)
        app.directive('tooltip', tooltip)
    },
}).then((app) => app.mount('#app'))
